import type { AnalyticsUserEvent } from '@fruitful/types';
import { StatsigClientExpo, type StatsigUser } from '@statsig/expo-bindings';

import { Singleton } from '../../common/utils/Singleton';
import { FRUITFUL_ENV, STATSIG_TOKEN } from '../../config/config';

export const StatsigConfig = {
    environment: { tier: FRUITFUL_ENV },
    // To avoid some clients filtering out requests directly to Statsig,
    // we set up a proxy to route requests through our platform.
    // In our DNS, this URL points to a Cloudfront distribution,
    // which triggers a Lambda@Edge function to rewrite the header to
    // Statsig's API endpoint.
    // The "/v1" path is required by Statsig
    // See docs: https://docs.statsig.com/custom_proxy
    networkConfig: { api: 'https://features.fruitful.com/v1' },
};

/*
 * A singleton class for exposing the StatsigClientExpo class to use statsig
 * in places where hooks are not available.
 *
 * @see https://docs.statsig.com/client/javascript-sdk/expo#expo-without-react
 */
class StatsigFacade extends Singleton {
    private statsigClient?: StatsigClientExpo;

    public logEvent(
        eventType: AnalyticsUserEvent,
        data?: Record<string, string | number | boolean>,
    ) {
        const stringifiedData = data
            ? Object.keys(data).reduce<Record<string, string>>((acc, key) => {
                  const d = data?.[key];
                  if (d != null) {
                      acc[key] = d.toString();
                  }
                  return acc;
              }, {})
            : undefined;
        this.statsigClient?.logEvent(eventType, undefined, stringifiedData);
        void this.statsigClient?.flush().catch(() => {});
    }

    public async init(user: StatsigUser) {
        if (this.statsigClient !== undefined) {
            return;
        }
        const statsigClient = new StatsigClientExpo(
            STATSIG_TOKEN,
            user,
            StatsigConfig,
        );
        await statsigClient.initializeAsync();
        this.statsigClient = statsigClient;
    }
}

export default StatsigFacade.getInstance();
